import { computed } from 'vue'
import CUSTOM_FIELD_TYPES from '@/constants/customFieldTypes'
import { isInputed } from '@/utilities/validator'

export default function(hearingFieldConfig?: HearingFieldConfig, inputHearingFields?: Inputs) {
  /**
   * ヒアリング項目の設定情報が渡されているかどうか
   */
  const hasHearingFields = computed<boolean>(() => {
    return !!hearingFieldConfig && !!hearingFieldConfig.sections.length
  })

  /**
   * 適切に設定されたヒアリング項目の入力オブジェクトが存在しているかどうか
   */
  const hasInputObjectForHearingFields = computed<boolean>(() => {
    if (!inputHearingFields) return false

    return !!Object.keys(inputHearingFields).filter(key => {
      return !!inputHearingFields[key].name
    }).length
  })

  /**
   * 回答済みのヒアリング項目が存在するかどうか
   *
   * @description 回答済みのヒアリング項目がある場合、資料詳細のプロフィール欄に表示させる
   * @returns 回答済み項目が一つでもある場合は true
   */
  const hasFilledItemsInHearingField = computed<boolean>(() => {
    if (!hasHearingFields.value) return false
    return hearingFieldConfig!.sections.some(section => section.items.some(item => isInputed(item.value)))
  })

  /**
   * 未回答のヒアリング項目が存在するかどうか
   *
   * @description 資料詳細のヒアリング項目入力フォームには、未回答の分のみ表示する
   * @returns 未回答項目が一つでもある場合は true
   */
  const hasBlankItemsInHearingField = computed<boolean>(() => {
    if (!hasHearingFields.value) return false
    return hearingFieldConfig!.sections.some(section => section.items.some(item => !isInputed(item.value)))
  })

  /**
   * 回答済みのヒアリング項目のみを抽出して返す
   * value が存在する項目のみを抽出する
   * @returns 回答済みのヒアリング項目セクション
   */
  const filteredHearingFieldSections = computed<HearingFieldConfig['sections']>(() => {
    if (!hasHearingFields.value) return []
    return hearingFieldConfig!.sections.filter(section => section.items.some(item => isInputed(item.value)))
  })

  // methods
  /**
   * HearingFieldConfigItem配列を文字列にフォーマットして返す
   * @param items 変換対象の値
   * @returns フォーマット済み文字列
   */
  const formatHearingFieldsAsString = (items: HearingFieldConfigItem[]): string => {
    return items
      .filter(item => !!item.value)
      .map(item => {
        switch (item.fieldType) {
          case CUSTOM_FIELD_TYPES.CHECKBOX_MULTI:
          case CUSTOM_FIELD_TYPES.SELECT_MULTI:
            return (item.value as string[]).join(', ')
          case CUSTOM_FIELD_TYPES.CHECKBOX:
            return (item.value as number) ? item.title : ''
          default:
            return item.value?.toString().trim() || ''
        }
      }).filter((v): v is string => !!v.length).join(', ')
  }

  return {
    // computed
    hasHearingFields,
    hasInputObjectForHearingFields,
    hasFilledItemsInHearingField,
    hasBlankItemsInHearingField,
    filteredHearingFieldSections,

    // methods
    formatHearingFieldsAsString,
  }
}
